import { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  Button,
  CountTag,
  Label,
  Select,
} from '@jem-open/jem-ui';
import { Loader2, ChevronDown, ChevronUp, Copy } from 'lucide-react';
import type { EmployerOption, EmployerPanel } from '../../types/bulkUpload';

interface SelectEmployerStepProps {
  employers: { id: number; company_name: string }[];
  selectedEmployer: EmployerOption | null;
  onEmployerChange: (option: EmployerOption | null) => void;
  loadingEmployerData: boolean;
  panels: EmployerPanel[];
}

const SelectEmployerStep = ({
  employers,
  selectedEmployer,
  onEmployerChange,
  loadingEmployerData,
  panels,
}: SelectEmployerStepProps) => {
  const [showValues, setShowValues] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const options = useMemo(
    () => employers.map((emp) => ({ value: String(emp.id), label: emp.company_name })),
    [employers],
  );

  const handleValueChange = (value: string) => {
    const emp = employers.find((e) => String(e.id) === value);
    if (emp) {
      onEmployerChange({ value: emp.id, label: emp.company_name });
    }
  };

  const handleCopy = async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((current) => (current === key ? null : current)), 1500);
    } catch {
      // Clipboard API can fail in non-secure contexts; ignore silently.
    }
  };

  return (
    <div className="flex flex-col gap-sm">
      <div className="flex flex-col gap-xxs max-w-[800px] w-full">
        <Label className="font-semibold shrink-0">Employer</Label>
        <Select
          searchable
          options={options}
          value={selectedEmployer ? String(selectedEmployer.value) : undefined}
          onValueChange={handleValueChange}
          placeholder="Select an employer..."
          searchPlaceholder="Search employers..."
          emptyMessage="No employers found."
        />
      </div>

      {selectedEmployer && loadingEmployerData && (
        <div className="flex items-center gap-xxs py-xxs">
          <Loader2 className="h-4 w-4 animate-spin text-primary-text-label" />
          <span className="text-sm text-greyscale-text-caption">Loading employer data...</span>
        </div>
      )}

      {selectedEmployer && !loadingEmployerData && (
        <>
          <div className="flex items-center [&>button]:pl-0">
            <Button
              variant="link"
              size="medium"
              rightIcon={showValues ? <ChevronUp className="size-[14px]" /> : <ChevronDown className="size-[14px]" />}
              onClick={() => setShowValues(!showValues)}
            >
              View available values for {selectedEmployer.label}
            </Button>
          </div>
          {showValues && (
            <div className="max-w-[800px] w-full">
              <p className="text-sm text-greyscale-text-caption mt-xs mb-xxs">
                Use semicolons to separate multiple values in the CSV. Click a value below to copy it.
              </p>
              <Accordion type="multiple">
                {panels.map((panel) => (
                  <AccordionItem key={panel.key} value={panel.key}>
                    <AccordionTrigger className="text-sm font-semibold">
                      <span className="flex items-center gap-xxs">
                        {panel.label}
                        <CountTag count={panel.items.length} />
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm">
                      {panel.key === 'receive_queries_from_branches' && panel.items.some((b) => b.code) && (
                        <p className="text-xs text-greyscale-text-caption mb-xxs">
                          Branches show as <em>Name (Code)</em>. Clicking copies the code when
                          available (otherwise the name) — the value the CSV expects.
                        </p>
                      )}
                      {panel.items.length > 0
                        ? (
                          <>
                            {(panel.key === 'features' || panel.key === 'receive_queries_from_branches' || panel.key === 'tags') && (() => {
                              const allKey = `${panel.key}-all`;
                              const isAllCopied = copiedKey === allKey;
                              const isBranchPanel = panel.key === 'receive_queries_from_branches';
                              const allValue = panel.items
                                .map((item) => (isBranchPanel ? (item.code || item.name) : item.name))
                                .filter(Boolean)
                                .join('; ');
                              return (
                                <div className="flex items-center justify-end mb-xxs">
                                  <Button
                                    variant="link"
                                    size="small"
                                    leftIcon={<Copy className="size-[12px]" />}
                                    onClick={() => handleCopy(allKey, allValue)}
                                  >
                                    {isAllCopied ? 'Copied!' : `Copy all ${panel.label.toLowerCase()}`}
                                  </Button>
                                </div>
                              );
                            })()}
                            <div className="flex flex-wrap gap-xxs">
                              {panel.items.map((item) => {
                                const label = `${item.name}${item.code ? ` (${item.code})` : ''}`;
                                const isBranchPanel = panel.key === 'receive_queries_from_branches';
                                const copyValue = isBranchPanel ? (item.code || item.name) : item.name;
                                const tagKey = `${panel.key}-${item.id}`;
                                const isCopied = copiedKey === tagKey;
                                return (
                                  <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => handleCopy(tagKey, copyValue)}
                                    title={`Click to copy "${copyValue}"`}
                                    aria-label={`Copy ${copyValue}`}
                                    className="rounded-md bg-greyscale-surface-subtle px-2 py-0.5 text-xs text-greyscale-text-body hover:brightness-95 cursor-pointer transition-colors"
                                  >
                                    {isCopied ? 'Copied!' : label}
                                  </button>
                                );
                              })}
                            </div>
                          </>
                        )
                        : <em className="text-greyscale-text-caption">None available for this employer</em>}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SelectEmployerStep;
